// @flow
import type { Element } from 'react';

import React from 'react';
import { Form, Table, UncontrolledAlert as Alert } from 'reactstrap';
import {
  CEILINGS_CAPACITY_MULTIPLIER,
  CEILINGS_COST_MULTIPLIER,
  ELEVATORS_CAPACITY_MULTIPLIER,
  ELEVATORS_COST_MULTIPLIER,
  EXTERIOR_FINISHES_CAPACITY_MULTIPLIER,
  EXTERIOR_FINISHES_COST_MULTIPLIER,
  EXTERIOR_WALLS_CAPACITY_MULTIPLIER,
  EXTERIOR_WALLS_COST_MULTIPLIER,
  HVAC_CAPACITY_MULTIPLIER,
  HVAC_COST_MULTIPLIER,
  INTERIOR_WALLS_CAPACITY_MULTIPLIER,
  INTERIOR_WALLS_COST_MULTIPLIER,
  LIGHTING_CAPACITY_MULTIPLIER,
  LIGHTING_COST_MULTIPLIER,
  PARTITION_WALLS_CAPACITY_MULTIPLIER,
  PARTITION_WALLS_COST_MULTIPLIER,
  PIPING_CAPACITY_MULTIPLIER,
  PIPING_COST_MULTIPLIER,
  STRUCTURAL_CAPACITY_MULTIPLIER,
  STRUCTURAL_COST_MULTIPLIER,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import BackButton from '#components/pages/Models/Model/sections/support/buttons/BackButton.jsx';
import NextButton from '#components/pages/Models/Model/sections/support/buttons/NextButton.jsx';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import { Field, FormGroup } from '#components/pages/Models/Model/support/form/index.js';

type ComponentRowProps = {|
  capacityName: string,
  costName: string,
  description: string,
  fieldsByName: Object,
  form: string,
|};

const ComponentMultiplierRow = ({
  capacityName,
  costName,
  description,
  fieldsByName,
  form,
}: ComponentRowProps) => (
  <FormGroup tag="tr" form={form} fields={[costName, capacityName]}>
    <td>{description}</td>
    <td>
      <Field field={fieldsByName[costName]} excludeLabel />
    </td>
    <td>
      <Field field={fieldsByName[capacityName]} excludeLabel />
    </td>
  </FormGroup>
);

type ComponentMultipliersProps = {|
  fieldsByName: Object,
  form: string,
|};

const ComponentMultipliers = ({ fieldsByName, form }: ComponentMultipliersProps) => (
  <div>
    <FormGroup tag="fieldset">
      <legend>Component Group Modifiers</legend>
      <Table responsive className="component-info">
        <thead>
          <tr>
            <th>Component Group</th>
            <th>
              Cost <Tip tipId="componentCostMultiplier" />
            </th>
            <th>
              Capacity <Tip tipId="componentCapacityMultiplier" />
            </th>
          </tr>
        </thead>
        <tbody>
          <ComponentMultiplierRow
            costName={INTERIOR_WALLS_COST_MULTIPLIER}
            capacityName={INTERIOR_WALLS_CAPACITY_MULTIPLIER}
            description="Interior Walls (Wood Light Frame)"
            fieldsByName={fieldsByName}
            form={form}
          />
          <ComponentMultiplierRow
            costName={EXTERIOR_WALLS_COST_MULTIPLIER}
            capacityName={EXTERIOR_WALLS_CAPACITY_MULTIPLIER}
            description="Exterior Walls (Wood Light Frame)"
            fieldsByName={fieldsByName}
            form={form}
          />
          <ComponentMultiplierRow
            costName={STRUCTURAL_COST_MULTIPLIER}
            capacityName={STRUCTURAL_CAPACITY_MULTIPLIER}
            description="Structural"
            fieldsByName={fieldsByName}
            form={form}
          />
          <ComponentMultiplierRow
            costName={PARTITION_WALLS_COST_MULTIPLIER}
            capacityName={PARTITION_WALLS_CAPACITY_MULTIPLIER}
            description="Partition Walls"
            fieldsByName={fieldsByName}
            form={form}
          />
          <ComponentMultiplierRow
            costName={EXTERIOR_FINISHES_COST_MULTIPLIER}
            capacityName={EXTERIOR_FINISHES_CAPACITY_MULTIPLIER}
            description="Exterior Finishes"
            fieldsByName={fieldsByName}
            form={form}
          />
          <ComponentMultiplierRow
            costName={CEILINGS_COST_MULTIPLIER}
            capacityName={CEILINGS_CAPACITY_MULTIPLIER}
            description="Ceilings"
            fieldsByName={fieldsByName}
            form={form}
          />
          <ComponentMultiplierRow
            costName={LIGHTING_COST_MULTIPLIER}
            capacityName={LIGHTING_CAPACITY_MULTIPLIER}
            description="Lighting"
            fieldsByName={fieldsByName}
            form={form}
          />
          <ComponentMultiplierRow
            costName={HVAC_COST_MULTIPLIER}
            capacityName={HVAC_CAPACITY_MULTIPLIER}
            description="HVAC"
            fieldsByName={fieldsByName}
            form={form}
          />
          <ComponentMultiplierRow
            costName={PIPING_COST_MULTIPLIER}
            capacityName={PIPING_CAPACITY_MULTIPLIER}
            description="Piping"
            fieldsByName={fieldsByName}
            form={form}
          />
          <ComponentMultiplierRow
            costName={ELEVATORS_COST_MULTIPLIER}
            capacityName={ELEVATORS_CAPACITY_MULTIPLIER}
            description="Elevators"
            fieldsByName={fieldsByName}
            form={form}
          />
        </tbody>
      </Table>
    </FormGroup>
  </div>
);

type BuildingComponentFormProps = {|
  error: string,
  fieldsByName: Object,
  form: string,
  handleSubmit: Function,
  heading: string,
  onBack: Function,
  onNext: Function,
  submitting: boolean,
|};

const BuildingComponentsSection = (props: BuildingComponentFormProps): Element<'div'> => {
  const { error, fieldsByName, form, handleSubmit, heading, onBack, onNext, submitting } = props;

  return (
    <div>
      <h2>{heading}</h2>
      <Form noValidate onSubmit={handleSubmit}>
        <ComponentMultipliers fieldsByName={fieldsByName} form={form} />
        {error && !submitting ? <Alert color="danger">{error}</Alert> : ''}
        <BackButton onClick={onBack} />
        <NextButton onClick={onNext} />
      </Form>
    </div>
  );
};

export default BuildingComponentsSection;
